import type { GlyphName, MenuEntry } from "../../ui/kit/index.ts";
import { say } from "../../words.ts";
import { sameName, type PartyName } from "../party/members.ts";
import { menuEntries, type PartyChoice } from "../party/rows.ts";
import type { PartySnapshot } from "../party/store.ts";

/**
 * The player menu's entries (ADR-101's note of 2026-10-16, P2.13-T18), as data the menu draws: what a menu on a
 * character offers the viewer, in the design card's order. The party's own entries come from `menuEntries`.
 */

/**
 * What a player menu entry does.
 */
export type PlayerChoice = PartyChoice | "whisper" | "trade" | "card" | "block" | "unblock" | "report";

/**
 * What the entries read besides the party: the families the viewer blocks and whether the viewer is in combat.
 */
export interface MenuContext {
  readonly party: PartySnapshot;
  readonly blocks: readonly string[];
  readonly inCombat: boolean;
}

/**
 * Whether the viewer blocks a character's family, ignoring case.
 *
 * @param blocks The blocked families.
 * @param subject The character.
 * @returns True when the family is blocked.
 */
export function isBlocked(blocks: readonly string[], subject: PartyName): boolean {
  const family = subject.familyName.toLowerCase();
  return family !== "" && blocks.some((blocked) => blocked.toLowerCase() === family);
}

/**
 * The entries of the menu for a character. On the viewer's own seat: Leave party alone. For a blocked family:
 * Unblock and Report, as the design's blocked state draws it. Otherwise Whisper (disabled for a member known
 * offline), the party's entries (Invite to party outside the party, disabled while the viewer is in combat; Make
 * leader and Can invite for the leader), Trade and View player card (listed, disabled: neither exists), then after a
 * divider Remove from party where the party offers it, Block and Report (disabled: no report exists yet).
 *
 * @param context The party, the blocked families and the viewer's combat state.
 * @param subject The character the menu is for.
 * @returns The entries.
 */
export function playerMenuEntries(context: MenuContext, subject: PartyName): readonly MenuEntry<PlayerChoice>[] {
  const { party } = context;
  const seat = party.members.find((member) => sameName(member, subject)) ?? null;
  const partyEntries = menuEntries(party, subject);
  if (seat !== null && party.self !== null && seat.place === party.self.place) {
    return partyEntries;
  }
  const soon = (value: PlayerChoice, label: string, glyph: GlyphName, extra: Partial<MenuEntry<PlayerChoice>> = {}): MenuEntry<PlayerChoice> => ({ value, label, glyph, disabled: true, why: say("party.menu.soon"), ...extra });
  const report = soon("report", say("party.menu.report"), "alert", { danger: true });
  if (isBlocked(context.blocks, subject)) {
    return [{ value: "unblock", label: say("party.menu.unblock"), glyph: "user-x" }, report];
  }
  const offline = seat !== null && !seat.online;
  const whisper: MenuEntry<PlayerChoice> = { value: "whisper", label: say("party.menu.whisper"), glyph: "note", disabled: offline, ...(offline ? { why: say("party.menu.offline") } : {}) };
  const actions = partyEntries
    .filter((entry) => entry.value !== "remove")
    .map((entry): MenuEntry<PlayerChoice> => (entry.value === "invite" && context.inCombat && entry.disabled !== true ? { ...entry, disabled: true, why: say("party.menu.combat") } : entry));
  const remove = partyEntries.filter((entry) => entry.value === "remove").map((entry) => ({ ...entry, divided: true }));
  const block: MenuEntry<PlayerChoice> = { value: "block", label: say("party.menu.block"), glyph: "user-x", danger: true, divided: remove.length === 0 };
  return [whisper, ...actions, soon("trade", say("party.menu.trade"), "coin"), soon("card", say("party.menu.card"), "user"), ...remove, block, report];
}
